import apiClient, { buildQueryParams } from '../utils/apiClient';
import { handleError } from '../utils/errorHandler';
import { API_ENDPOINTS } from '../constants/apiEndpoints.constants';

/**
 * Service for Admin API calls
 */
const adminService = {
  /**
   * Liste paginée de tous les utilisateurs (RBAC)
   */
  async getUsers(params = {}) {
    try {
      const queryParams = buildQueryParams({
        page: params.page || 1,
        limit: params.limit || 20,
        search: params.search,
        role: params.role,
        accountStatus: params.accountStatus,
        sortBy: params.sortBy,
        sortOrder: params.sortOrder,
        minimal: params.minimal,
      });

      const response = await apiClient.get(`${API_ENDPOINTS.ADMIN_USERS.BASE}?${queryParams}`);

      return {
        success: true,
        data: response.data || [],
        pagination: response.pagination,
      };
    } catch (error) {
      throw new Error(handleError(error, 'adminService.getUsers'));
    }
  },

  /**
   * Get users with KYC pending
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>}
   */
  async getUsersWithKYC(params = {}) {
    try {
      const queryParams = buildQueryParams({
        page: params.page || 1,
        limit: params.limit || 20,
        search: params.search,
        kycStatus: params.kycStatus,
      });

      const response = await apiClient.get(`${API_ENDPOINTS.ADMIN_USERS.KYC}?${queryParams}`);
      
      return {
        success: true,
        data: response.data || [],
        pagination: response.pagination,
      };
    } catch (error) {
      throw new Error(handleError(error, 'adminService.getUsersWithKYC'));
    }
  },

  /**
   * Get all details of a specific user
   * @param {string} userId - User ID
   * @returns {Promise<Object>}
   */
  async getUserDetails(userId) {
    try {
      const response = await apiClient.get(API_ENDPOINTS.ADMIN_USERS.GET_BY_ID(userId));
      
      return {
        success: true,
        data: response.data || null,
      };
    } catch (error) {
      throw new Error(handleError(error, 'adminService.getUserDetails'));
    }
  },

  /**
   * Vérifier ou révoquer la vérification e-mail / téléphone d'un utilisateur
   */
  async verifyUserContact(userId, field, value) {
    try {
      const response = await apiClient.patch(API_ENDPOINTS.ADMIN_USERS.VERIFY_CONTACT, {
        userId,
        field,
        value,
      });

      return {
        success: true,
        data: response.data?.data || response.data,
        message: response.data?.message,
      };
    } catch (error) {
      throw new Error(handleError(error, 'adminService.verifyUserContact'));
    }
  },

  async updateUserRole(userId, role) {
    try {
      const response = await apiClient.patch(API_ENDPOINTS.ADMIN_USERS.UPDATE_ROLE(userId), { role });
      return {
        success: true,
        data: response.data || response,
        message: response.message,
      };
    } catch (error) {
      throw new Error(handleError(error, 'adminService.updateUserRole'));
    }
  },

  async updateUserStatus(userId, accountStatus, reason) {
    try {
      const response = await apiClient.patch(API_ENDPOINTS.ADMIN_USERS.UPDATE_STATUS(userId), {
        accountStatus,
        reason,
      });
      return {
        success: true,
        data: response.data || response,
        message: response.message,
      };
    } catch (error) {
      throw new Error(handleError(error, 'adminService.updateUserStatus'));
    }
  },

  async updateUserPermissions(userId, permissions) {
    try {
      const response = await apiClient.patch(
        API_ENDPOINTS.ADMIN_USERS.UPDATE_PERMISSIONS(userId),
        { permissions }
      );
      return {
        success: true,
        data: response.data || response,
        message: response.message,
      };
    } catch (error) {
      throw new Error(handleError(error, 'adminService.updateUserPermissions'));
    }
  },

  async createUser(payload) {
    try {
      const response = await apiClient.post(API_ENDPOINTS.ADMIN_USERS.BASE, payload);
      return {
        success: true,
        data: response.data || response,
        message: response.message,
      };
    } catch (error) {
      throw new Error(handleError(error, 'adminService.createUser'));
    }
  },

  async deleteUser(userId) {
    try {
      const response = await apiClient.delete(API_ENDPOINTS.ADMIN_USERS.DELETE(userId));
      return {
        success: true,
        data: response.data || response,
        message: response.message,
      };
    } catch (error) {
      throw new Error(handleError(error, 'adminService.deleteUser'));
    }
  },
};

export default adminService;
